import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  TrendingUp, BarChart3, Users, Calendar, Eye, MousePointer, 
  Flame, ArrowUpRight, RefreshCw, Loader2, Target, ChevronRight,
  Zap, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTrendingPage() {
  const [overview, setOverview] = useState(null);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [filterCity, setFilterCity] = useState('all');
  
  const token = localStorage.getItem('admin_token');

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [overviewRes, trendingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/trending?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setOverview(overviewRes.data);
      setTrending(trendingRes.data.trending || []);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      await axios.post(
        `${BACKEND_URL}/api/admin/analytics/recalculate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Popularity scores recalculated');
      fetchData();
    } catch (err) {
      toast.error('Failed to recalculate scores');
    } finally {
      setRecalculating(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const filteredTrending = filterCity === 'all' 
    ? trending 
    : trending.filter(e => e.citySlug === filterCity);

  const uniqueCities = [...new Set(trending.map(e => e.citySlug).filter(Boolean))];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Flame className="w-7 h-7 text-orange-600" />
              Trending & Predictions
            </h1>
            <p className="text-gray-600 mt-1">
              Real-time popularity scores and attendance forecasts
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={handleRecalculate}
              disabled={recalculating}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {recalculating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Recalculate All
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Trending Events</p>
                  <p className="text-3xl font-bold">
                    {trending.filter(e => e.trending).length}
                  </p>
                </div>
                <Flame className="w-10 h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Page Views (7d)</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(overview?.summary?.pageViews7d || 0)}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Unique Visitors</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(overview?.summary?.uniqueVisitors7d || 0)}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Partner Clicks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(overview?.summary?.partnerClicks7d || 0)}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <MousePointer className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Views Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Traffic Trend
            </CardTitle>
            <CardDescription>Daily page views over the last 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={overview?.dailyViews || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(val) => val?.slice(5) || ''} 
                    fontSize={12}
                  />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#f97316" 
                    strokeWidth={3}
                    dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Trending Events Leaderboard */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  Event Leaderboard
                </CardTitle>
                <CardDescription>
                  Events ranked by popularity score with attendance forecasts
                </CardDescription>
              </div>
              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {uniqueCities.map(city => (
                    <SelectItem key={city} value={city} className="capitalize">
                      {city?.replace('-', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-center">
                    <span className="flex items-center justify-center gap-1">
                      <Zap className="w-3 h-3" /> Score
                    </span>
                  </TableHead>
                  <TableHead className="text-center">Views</TableHead>
                  <TableHead className="text-center">Velocity</TableHead>
                  <TableHead>Forecast</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTrending.map((event, idx) => (
                  <TableRow key={event.id} className={event.trending ? 'bg-orange-50/50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {idx < 3 ? (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 
                            idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : 
                            'bg-gradient-to-br from-orange-400 to-orange-600'
                          }`}>
                            {idx + 1}
                          </div>
                        ) : (
                          <span className="w-8 text-center text-gray-500">{idx + 1}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{event.name?.slice(0, 30)}{event.name?.length > 30 ? '...' : ''}</span>
                          {event.trending && (
                            <Badge className="bg-orange-500 text-white text-xs animate-pulse">
                              <Flame className="w-3 h-3 mr-1" />
                              Hot
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-1 mt-1">
                          {event.sectorTags?.slice(0, 2).map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{event.city}</p>
                        <p className="text-gray-500 text-xs">{event.venueName?.slice(0, 20)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full font-bold ${
                        event.popularityScore >= 60 ? 'bg-green-100 text-green-700' :
                        event.popularityScore >= 30 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {event.popularityScore?.toFixed(0) || '0'}
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {event.signals?.recentViews || 0}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`flex items-center justify-center gap-1 font-medium ${
                        event.signals?.viewVelocity > 1.5 ? 'text-green-600' :
                        event.signals?.viewVelocity > 1 ? 'text-yellow-600' :
                        'text-gray-500'
                      }`}>
                        {event.signals?.viewVelocity > 1 && <TrendingUp className="w-4 h-4" />}
                        {event.signals?.viewVelocity?.toFixed(1) || '0'}x
                      </span>
                    </TableCell>
                    <TableCell>
                      {event.forecast?.forecast ? (
                        <div>
                          <div className="text-sm font-medium">
                            {formatNumber(event.forecast.forecast.mid)} expected
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatNumber(event.forecast.forecast.low)} - {formatNumber(event.forecast.forecast.high)}
                          </div>
                          <Badge 
                            variant="outline" 
                            className={`text-xs mt-1 ${
                              event.forecast.confidence === 'high' ? 'border-green-500 text-green-600' :
                              event.forecast.confidence === 'medium' ? 'border-yellow-500 text-yellow-600' :
                              'border-gray-400 text-gray-500'
                            }`}
                          >
                            {event.forecast.confidence} confidence
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link 
                        to={`/admin/events/${event.id}`}
                        className="p-2 hover:bg-gray-100 rounded-full inline-flex"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredTrending.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No trending data available</p>
                <p className="text-sm mt-1">Events will appear here as they gain traction</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sectors & Cities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Sectors</CardTitle>
              <CardDescription>By page views this week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview?.topSectors || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" fontSize={12} />
                    <YAxis 
                      dataKey="sector" 
                      type="category" 
                      width={100} 
                      fontSize={11}
                      tickFormatter={(val) => val?.slice(0, 12) || ''}
                    />
                    <Tooltip />
                    <Bar dataKey="views" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Cities</CardTitle>
              <CardDescription>By page views this week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(overview?.topCities || []).map((city, idx) => (
                  <div key={city.city} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>{idx + 1}</span>
                      <span className="font-medium capitalize">{city.city?.replace('-', ' ')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full"
                          style={{ width: `${Math.min((city.views / (overview.topCities[0]?.views || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600 w-12 text-right">{city.views}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
